import { generateClient, GraphQLQuery } from 'aws-amplify/api';
import { Registrar } from '../../registrars/types';
import { 
    showErrorNotifications, 
    showSuccessNotification 
} from '../../../components/ServiceNotifications';

// GraphQL mutations for registrar operations
const createRegistrarMutation = `
    mutation createRegistrar($input: CreateRegistrarInput!) {
       createRegistrar(input:$input) {
            id
            name
            description
            website
            createdByUsername
            created
            updated
       }
}`;

const listRegistrarsQuery = `
    query listRegistrars($filter: ModelRegistrarFilterInput) {
        listRegistrars(filter: $filter) {
            items {
                id
                name
                description
                website
                createdByUsername
                created
                updated
            }
        }
    }
`;

// Type definitions
export type CreateRegistrarResponse = {
    createRegistrar: Registrar;
};

export type ListRegistrarsResponse = {
    listRegistrars: { items: Registrar[] };
};

export interface CreateRegistrarInput {
    name: string;
    description?: string;
    website?: string;
}

const client = generateClient();

/**
 * Service for managing registrar operations during domain import
 */
export class RegistrarService {
    /**
     * Create a new registrar
     */
    static async createRegistrar(input: CreateRegistrarInput): Promise<Registrar | undefined> {
        try {
            // Backend automatically extracts user context from authentication token
            // No need to pass createdByUsername explicitly
            const response = await client.graphql<GraphQLQuery<CreateRegistrarResponse>>({
                query: createRegistrarMutation,
                variables: { input: input }
            });

            if (response.data?.createRegistrar) {
                showSuccessNotification('Registrar created successfully');
                return response.data.createRegistrar;
            }
        } catch (error) {
            console.error('Failed to create registrar:', error);
            showErrorNotifications(error);
            throw error;
        }
    }

    /**
     * Get all available registrars
     */
    static async getAvailableRegistrars(): Promise<Registrar[]> {
        try {
            const response = await client.graphql<GraphQLQuery<ListRegistrarsResponse>>({
                query: listRegistrarsQuery
            });

            return response.data?.listRegistrars.items || [];
        } catch (error) {
            console.error('Failed to fetch registrars:', error);
            showErrorNotifications(error);
            return [];
        }
    }

    /**
     * Check if a registrar name already exists
     */
    static async checkRegistrarExists(name: string): Promise<boolean> {
        try {
            const registrars = await this.getAvailableRegistrars();
            return registrars.some(registrar => 
                registrar.name?.toLowerCase() === name.toLowerCase()
            );
        } catch (error) {
            console.error('Failed to check registrar existence:', error);
            return false;
        }
    }

    /**
     * Validate registrar name
     */
    static validateRegistrarName(name: string): { isValid: boolean; error?: string } {
        if (!name || name.trim().length === 0) {
            return { isValid: false, error: 'Registrar name is required' };
        }

        if (name.trim().length < 2) {
            return { isValid: false, error: 'Registrar name must be at least 2 characters long' };
        }

        if (name.trim().length > 100) {
            return { isValid: false, error: 'Registrar name must be less than 100 characters' };
        }

        // Check for valid characters (letters, numbers, spaces, hyphens, dots)
        const validNameRegex = /^[a-zA-Z0-9\s\-.]+$/;
        if (!validNameRegex.test(name.trim())) {
            return { isValid: false, error: 'Registrar name can only contain letters, numbers, spaces, hyphens, and dots' };
        }

        return { isValid: true };
    }
}